import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  Sparkles, Flower2, Moon, Droplet, Leaf, Wind, Shield, HeartPulse, Compass,
  BookHeart, Sun, Baby, MessageCircle, CalendarHeart, Star, ChevronLeft, ChevronRight,
  ArrowRight, CheckCircle2, Quote, Phone,
} from "lucide-react";
import { SITE, TREATMENTS, WHY_US, TESTIMONIALS, BLOG, FAQS } from "@/lib/site";
import { useSEO } from "@/lib/seo";
import SectionHeading from "@/components/SectionHeading";
import ClinicGallery from "@/components/ClinicGallery";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const iconMap = { Sparkles, Flower2, Moon, Droplet, Leaf, Wind, Shield, HeartPulse, Compass, BookHeart, Sun, Baby };

export default function Home() {
  useSEO({
    title: "Ayurveda for Women's Health & Fertility",
    description: "Nari Arogya, led by Dr. Anita Chaudhari — Ayurvedic Gynecologist & Fertility Specialist in Kothrud, Pune. Natural, personalized care for PCOS, infertility, pregnancy & women's wellness.",
    path: "/",
  });

  const waLink = `https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent("Hello Dr. Anita, I'd like to book a consultation.")}`;

  return (
    <>
      {/* HERO */}
      <section className="lotus-bg pt-32 pb-20 md:pt-40 md:pb-28 relative overflow-hidden" data-testid="hero-section">
        <div className="na-container grid gap-12 md:gap-16 lg:grid-cols-2 items-center">
          <div className="fade-up">
            <span className="eyebrow" data-testid="hero-eyebrow">Nari Arogya · Pune</span>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl leading-[1.05]" data-testid="hero-heading">
              Ayurveda for <span className="italic text-[color:var(--na-maroon)]">Women's Health</span> &amp; <span className="gold-underline">Fertility</span>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-[color:var(--na-muted)] max-w-xl leading-relaxed">
              {SITE.subtag}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/book" className="btn-primary" data-testid="hero-book-btn">
                <CalendarHeart size={18} /> Book Appointment
              </Link>
              <a href={waLink} target="_blank" rel="noreferrer" className="btn-outline" data-testid="hero-whatsapp-btn">
                <MessageCircle size={18} /> WhatsApp Now
              </a>
            </div>

            <div className="mt-10 flex items-center gap-6 flex-wrap">
              <div className="flex items-center gap-2">
                <div className="flex">
                  {[...Array(5)].map((_, i) => <Star key={i} size={18} fill="#C9A227" strokeWidth={0} />)}
                </div>
                <span className="text-sm text-[color:var(--na-muted)]">5.0 · 21 verified Google reviews</span>
              </div>
              <div className="h-6 w-px bg-[color:var(--na-border)]" />
              <div className="text-sm text-[color:var(--na-muted)]">
                <span className="text-[color:var(--na-text)] font-semibold">10+ yrs</span> in Ayurvedic Gynecology
              </div>
            </div>
          </div>

          <div className="relative fade-up-delay-2">
            <div className="absolute -inset-6 rounded-[2.5rem] bg-gradient-to-br from-[color:var(--na-teal)]/15 via-transparent to-[color:var(--na-maroon)]/10 blur-2xl" />
            <div className="relative rounded-[2rem] overflow-hidden bg-white border border-[color:var(--na-border)] shadow-[0_30px_80px_-30px_rgba(76,157,166,0.35)]">
              <img
                src={SITE.doctorPhoto}
                alt={SITE.doctor}
                className="w-full h-[520px] md:h-[600px] object-cover object-top"
                data-testid="hero-doctor-photo"
              />
              <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur border border-[color:var(--na-border)] rounded-2xl p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[color:var(--na-teal)]/10 text-[color:var(--na-teal)] flex items-center justify-center">
                  <HeartPulse size={20} />
                </div>
                <div>
                  <p className="font-semibold text-sm">{SITE.doctor}</p>
                  <p className="text-xs text-[color:var(--na-muted)]">{SITE.role}</p>
                </div>
              </div>
            </div>
            {/* Floating badge */}
            <div className="hidden md:flex absolute -left-6 top-8 floaty bg-white shadow-lg rounded-2xl px-4 py-3 items-center gap-3 border border-[color:var(--na-border)]">
              <Sparkles size={18} className="text-[color:var(--na-gold)]" />
              <span className="text-sm font-semibold">100% Natural</span>
            </div>
            <div className="hidden md:flex absolute -right-4 top-1/2 floaty bg-white shadow-lg rounded-2xl px-4 py-3 items-center gap-3 border border-[color:var(--na-border)]">
              <Flower2 size={18} className="text-[color:var(--na-teal)]" />
              <span className="text-sm font-semibold">Classical Ayurveda</span>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="section section-alt" id="services" data-testid="services-section">
        <div className="na-container">
          <SectionHeading
            eyebrow="Our Treatments"
            title="Comprehensive care for every stage"
            subtitle="From preconception to menopause, Nari Arogya offers root-cause Ayurvedic solutions tailored to your prakriti and lifestyle."
            align="center"
          />
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {TREATMENTS.map((t, idx) => {
              const Icon = iconMap[t.icon] || Sparkles;
              return (
                <Link
                  to={`/treatments/${t.slug}`}
                  key={t.slug}
                  className="na-card p-7 group"
                  style={{ animationDelay: `${idx * 60}ms` }}
                  data-testid={`service-card-${t.slug}`}
                >
                  <div className="w-14 h-14 rounded-2xl bg-[color:var(--na-teal)]/10 text-[color:var(--na-teal)] flex items-center justify-center group-hover:bg-[color:var(--na-maroon)] group-hover:text-white transition">
                    <Icon size={26} />
                  </div>
                  <h3 className="mt-5 text-2xl">{t.title}</h3>
                  <p className="mt-2 text-sm text-[color:var(--na-muted)] leading-relaxed">{t.short}</p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[color:var(--na-teal)] group-hover:text-[color:var(--na-maroon)] transition">
                    Learn more <ArrowRight size={14} />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ABOUT DOCTOR */}
      <section className="section" data-testid="about-section">
        <div className="na-container grid gap-12 lg:grid-cols-2 items-center">
          <div className="relative order-2 lg:order-1">
            <div className="absolute -top-4 -left-4 w-40 h-40 rounded-[2rem] border-2 border-[color:var(--na-gold)]" aria-hidden />
            <div className="absolute -bottom-4 -right-4 w-40 h-40 rounded-[2rem] bg-[color:var(--na-teal)]/10" aria-hidden />
            <img
              src={SITE.doctorPhoto}
              alt={SITE.doctor}
              className="relative rounded-[2rem] w-full h-[540px] object-cover object-top shadow-xl"
              data-testid="about-doctor-photo"
            />
          </div>

          <div className="order-1 lg:order-2">
            <span className="eyebrow">About Doctor</span>
            <h2 className="mt-4 text-4xl sm:text-5xl leading-tight">Meet <span className="italic text-[color:var(--na-maroon)]">Dr. Anita Chaudhari</span></h2>
            <p className="mt-3 text-lg text-[color:var(--na-teal)] font-semibold">{SITE.credentials}</p>
            <p className="text-sm uppercase tracking-wider text-[color:var(--na-muted)]">{SITE.role}</p>
            <p className="mt-6 text-base text-[color:var(--na-muted)] leading-relaxed">
              Dedicated to helping women achieve hormonal balance, reproductive wellness, and healthy pregnancies through authentic Ayurveda and personalized care. Every treatment plan is crafted after understanding your unique constitution, lifestyle and goals.
            </p>

            <div className="mt-8 grid grid-cols-2 gap-4">
              {WHY_US.map((w, i) => (
                <div key={w.title} className="flex items-start gap-3 fade-up" style={{ animationDelay: `${i * 60}ms` }}>
                  <CheckCircle2 size={20} className="text-[color:var(--na-teal)] shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-sm">{w.title}</p>
                    <p className="text-xs text-[color:var(--na-muted)] leading-relaxed mt-0.5">{w.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/about" className="btn-primary" data-testid="about-read-more">
                Read Full Bio <ArrowRight size={16} />
              </Link>
              <Link to="/book" className="btn-outline">
                Book a Consultation
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CLINIC GALLERY */}
      <ClinicGallery />

      {/* TESTIMONIALS */}
      <TestimonialSlider />

      {/* BLOG */}
      <section className="section" id="blog" data-testid="blog-section">
        <div className="na-container">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
            <SectionHeading
              eyebrow="From the Journal"
              title="Wisdom for women's wellness"
              subtitle="Insights on fertility, PCOS, pregnancy and Ayurveda — written by Dr. Anita."
            />
            <Link to="/blog" className="btn-outline text-sm">All articles <ArrowRight size={14} /></Link>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {BLOG.slice(0, 3).map((b) => (
              <Link to={`/blog/${b.slug}`} key={b.slug} className="na-card overflow-hidden group" data-testid={`blog-card-${b.slug}`}>
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={b.cover} alt={b.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-700" loading="lazy" />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 text-xs text-[color:var(--na-muted)]">
                    <span className="px-2.5 py-1 rounded-full bg-[color:var(--na-teal)]/10 text-[color:var(--na-teal)] font-semibold">{b.tag}</span>
                    <span>{b.date}</span>
                    <span>·</span>
                    <span>{b.read}</span>
                  </div>
                  <h3 className="mt-3 text-2xl leading-snug group-hover:text-[color:var(--na-teal)] transition">{b.title}</h3>
                  <p className="mt-2 text-sm text-[color:var(--na-muted)] leading-relaxed">{b.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section section-alt" id="faq" data-testid="faq-section">
        <div className="na-container grid gap-12 lg:grid-cols-2">
          <div>
            <SectionHeading
              eyebrow="Frequently Asked"
              title="Answers to what you're wondering"
              subtitle="Common questions about PCOS, fertility, pregnancy and Ayurvedic care. Can't find your answer? Just message us."
            />
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/faqs" className="btn-outline">See all FAQs <ArrowRight size={14} /></Link>
              <a href={waLink} target="_blank" rel="noreferrer" className="btn-primary">
                <MessageCircle size={16} /> Ask on WhatsApp
              </a>
            </div>
          </div>
          <div>
            <Accordion type="single" collapsible className="space-y-3">
              {FAQS.slice(0, 6).map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="na-card px-5 border-none" data-testid={`faq-item-${i}`}>
                  <AccordionTrigger className="text-left text-base font-semibold py-4 hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-[color:var(--na-muted)] leading-relaxed pb-4">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CONTACT / CTA */}
      <section className="section" data-testid="contact-cta">
        <div className="na-container">
          <div className="relative overflow-hidden rounded-[2rem] p-10 md:p-16 bg-gradient-to-br from-[color:var(--na-teal)] to-[#3a7f87]">
            <div className="absolute -right-24 -bottom-24 w-96 h-96 rounded-full bg-white/10" aria-hidden />
            <div className="absolute -left-16 -top-16 w-64 h-64 rounded-full bg-[color:var(--na-gold)]/20" aria-hidden />
            <div className="relative grid gap-8 lg:grid-cols-2 items-center">
              <div>
                <span className="text-white/80 uppercase text-xs tracking-[0.22em] font-semibold">Take the first step</span>
                <h2 className="mt-4 text-4xl sm:text-5xl text-white">Your wellness journey begins with a conversation.</h2>
                <p className="mt-4 text-white/85 text-base max-w-xl leading-relaxed">
                  Book a consultation with Dr. Anita — in-person at our Kothrud clinic or online. Every plan is crafted around you.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row lg:flex-col gap-3 lg:items-end">
                <Link to="/book" className="btn-gold justify-center" data-testid="cta-book">
                  <CalendarHeart size={16} /> Book Appointment
                </Link>
                <a href={`tel:${SITE.phone}`} className="inline-flex items-center gap-2 rounded-full px-6 py-3 bg-white text-[color:var(--na-text)] font-semibold hover:bg-[color:var(--na-maroon)] hover:text-white transition">
                  <Phone size={16} /> {SITE.phone}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function TestimonialSlider() {
  const [idx, setIdx] = useState(0);
  const perView = 1;
  const total = TESTIMONIALS.length;

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % total), 6000);
    return () => clearInterval(t);
  }, [total]);

  const prev = () => setIdx((i) => (i - 1 + total) % total);
  const next = () => setIdx((i) => (i + 1) % total);

  return (
    <section className="section section-alt" id="reviews" data-testid="testimonials-section">
      <div className="na-container">
        <SectionHeading
          eyebrow="Google Reviews · 5.0 ★"
          title="Real women. Real transformations."
          subtitle="These are verified Google reviews from women who trusted us with their care."
          align="center"
        />

        <div className="mt-14 relative">
          <div className="max-w-3xl mx-auto na-card p-8 md:p-12 relative" data-testid="testimonial-card">
            <Quote size={48} className="text-[color:var(--na-teal)]/20 absolute top-6 right-6" />
            <div className="flex gap-1 mb-4">
              {[...Array(TESTIMONIALS[idx].rating)].map((_, i) => (
                <Star key={i} size={20} fill="#C9A227" strokeWidth={0} />
              ))}
            </div>
            <p className="text-xl md:text-2xl leading-relaxed font-display italic text-[color:var(--na-text)]">
              "{TESTIMONIALS[idx].text}"
            </p>
            <div className="mt-6 flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-[color:var(--na-maroon)]/10 text-[color:var(--na-maroon)] flex items-center justify-center font-semibold">
                {TESTIMONIALS[idx].name.charAt(0)}
              </div>
              <div>
                <p className="font-semibold">{TESTIMONIALS[idx].name}</p>
                <p className="text-xs text-[color:var(--na-muted)]">{TESTIMONIALS[idx].location}</p>
              </div>
            </div>
          </div>

          <div className="mt-6 flex items-center justify-center gap-3">
            <button onClick={prev} className="w-10 h-10 rounded-full border border-[color:var(--na-border)] flex items-center justify-center hover:bg-[color:var(--na-teal)] hover:text-white hover:border-[color:var(--na-teal)] transition" aria-label="Previous" data-testid="testimonial-prev">
              <ChevronLeft size={18} />
            </button>
            <div className="flex gap-1.5">
              {TESTIMONIALS.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setIdx(i)}
                  className={`h-2 rounded-full transition-all ${i === idx ? "w-8 bg-[color:var(--na-maroon)]" : "w-2 bg-[color:var(--na-border)]"}`}
                  aria-label={`Go to review ${i + 1}`}
                  data-testid={`testimonial-dot-${i}`}
                />
              ))}
            </div>
            <button onClick={next} className="w-10 h-10 rounded-full border border-[color:var(--na-border)] flex items-center justify-center hover:bg-[color:var(--na-teal)] hover:text-white hover:border-[color:var(--na-teal)] transition" aria-label="Next" data-testid="testimonial-next">
              <ChevronRight size={18} />
            </button>
          </div>

          <div className="mt-8 text-center">
            <a
              href={SITE.gmbUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[color:var(--na-teal)] hover:text-[color:var(--na-maroon)] transition"
              data-testid="google-reviews-link"
            >
              See all 21 Google reviews <ArrowRight size={14} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
